import React, { FunctionComponent } from 'react'
import { Portal } from 'react-native-portalize'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { NAV_HEADER_HEIGHT } from '@lyrahealth-inc/shared-app-logic/src/features/header/constants'

import { GlobalHeaderProps, HeaderRow, useHeaderContents } from './globalHeaderParts'
import { FloatingTabBar, FloatingTabBarTab } from '../../molecules/floatingTabBar/FloatingTabBar'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { GradientHeader } from '../gradientHeader/GradientHeader'

// A single container for both scroll states: swapping container types would remount the header contents,
// which resets stateful children such as the open messenger popover and its unsent draft (CPR-3208)
const HeaderContainer = styled.View<{ theme: ThemeType; isScrolled: boolean }>(({ isScrolled }) => ({
  width: '100%',
  height: NAV_HEADER_HEIGHT,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  flexDirection: 'column',
  justifyContent: 'flex-start',
  backgroundColor: 'transparent',
  ...(isScrolled && {
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  }),
}))

export const GlobalHeader: FunctionComponent<GlobalHeaderProps> = (props) => {
  const {
    isScrolled,
    navItems,
    careNavigatorModal,
    renderBottomNavInPortal = false,
    useCenteredTabSpacing = false,
  } = props
  const { breakpoints } = useTheme() as ThemeType
  const { headerContents, activeNavTab } = useHeaderContents(props)

  const floatingTabBar =
    navItems && !breakpoints.isMinWidthLaptop && activeNavTab ? (
      <FloatingTabBar
        testID={tID('GlobalHeaderFloatingTabBar')}
        tabs={navItems
          .filter((item) => item.condition?.() ?? true)
          .map(
            (item): FloatingTabBarTab => ({
              key: item.id,
              label: item.text,
              icon: (isFocused, iconColor) => item.floatingIcon?.(iconColor) ?? item.icon?.(isFocused) ?? null,
              onPress: item.onPressItem ?? noop,
              testID: tID(`GlobalHeaderFloatingTabBar-tab-${item.id}`),
            }),
          )}
        activeTabKey={activeNavTab.id}
        useCenteredTabSpacing={useCenteredTabSpacing}
      />
    ) : null

  return (
    <>
      <HeaderContainer isScrolled={isScrolled}>
        {isScrolled && <GradientHeader />}
        <HeaderRow testID={tID('HeaderRow')}>{headerContents}</HeaderRow>
      </HeaderContainer>
      {careNavigatorModal}
      {renderBottomNavInPortal && floatingTabBar ? <Portal>{floatingTabBar}</Portal> : floatingTabBar}
    </>
  )
}

export type { GlobalHeaderProps }
